import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Typography,
  Input,
  Select,
  Tooltip,
  Badge,
  Row,
  Col,
  Breadcrumb,
  Drawer,
  Descriptions,
  Popconfirm,
} from 'antd';
import {
  EyeOutlined,
  UserAddOutlined,
  FileTextOutlined,
  CloseCircleOutlined,
  MessageOutlined,
  SearchOutlined,
  HomeOutlined,
  ClockCircleOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import authApi from '../services/authApi';

const { Title, Text, Paragraph } = Typography;
const { Search } = Input;

interface QuoteRequest {
  id: number;
  customer_id: number;
  vehicle_id?: number;
  service_type: string;
  description: string;
  urgency: 'normal' | 'urgent' | 'emergency';
  status: 'pending' | 'assigned' | 'quoted' | 'declined';
  assigned_to?: number;
  estimate_id?: number;
  customer_notes?: string;
  preferred_contact_time?: string;
  created_at: string;
  updated_at: string;
  customer?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  };
  vehicle?: {
    id: number;
    year: number;
    make: string;
    model: string;
    vin: string;
  };
  assigned_user?: {
    id: number;
    first_name: string;
    last_name: string;
  };
}

const QuoteRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [requests, setRequests] = useState<QuoteRequest[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [searchText, setSearchText] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<QuoteRequest | null>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);

  // Determine the role prefix from current path
  const getRolePrefix = () => {
    if (location.pathname.startsWith('/owner')) return '/owner';
    if (location.pathname.startsWith('/advisor')) return '/advisor';
    return '/admin';
  };

  const rolePrefix = getRolePrefix();

  useEffect(() => {
    fetchRequests();
    fetchUsers();
  }, [statusFilter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (statusFilter) params.status = statusFilter;

      const response = await authApi.get('/quote-requests/admin', { params });
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching quote requests:', error);
      message.error('Failed to load quote requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await authApi.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleAssign = async (requestId: number, userId: number) => {
    try {
      await authApi.patch(
        `/quote-requests/admin/${requestId}/assign`,
        { user_id: userId }
      );
      message.success('Quote request assigned successfully');
      fetchRequests();
    } catch (error) {
      console.error('Error assigning quote request:', error);
      message.error('Failed to assign quote request');
    }
  };

  const handleCreateEstimate = async (request: QuoteRequest) => {
    try {
      navigate(`${rolePrefix}/estimates/new`, {
        state: {
          customerId: request.customer_id,
          vehicleId: request.vehicle_id,
          description: `${request.service_type}: ${request.description}`,
          quoteRequestId: request.id,
        }
      });
    } catch (error) {
      console.error('Error preparing estimate creation:', error);
      message.error('Failed to prepare estimate creation');
    }
  };

  const handleUpdateStatus = async (requestId: number, status: string) => {
    try {
      await authApi.put(
        `/quote-requests/admin/${requestId}`,
        { status }
      );
      message.success('Status updated successfully');
      fetchRequests();
      setDetailsVisible(false);
    } catch (error) {
      console.error('Error updating status:', error);
      message.error('Failed to update status');
    }
  };

  const viewDetails = (request: QuoteRequest) => {
    setSelectedRequest(request);
    setDetailsVisible(true);
  };

  const getUrgencyTag = (urgency: string) => {
    const config: any = {
      normal: { color: 'default', label: 'Normal' },
      urgent: { color: 'warning', label: 'Urgent' },
      emergency: { color: 'error', label: 'Emergency' },
    };
    const { color, label } = config[urgency] || config.normal;
    return <Tag color={color}>{label.toUpperCase()}</Tag>;
  };

  const getStatusTag = (status: string) => {
    const config: any = {
      pending: { color: 'gold', label: 'Pending' },
      assigned: { color: 'blue', label: 'Assigned' },
      quoted: { color: 'green', label: 'Quoted' },
      declined: { color: 'red', label: 'Declined' },
    };
    const { color, label } = config[status] || config.pending;
    return <Tag color={color}>{label}</Tag>;
  };

  const filteredRequests = requests.filter(request => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    const customerName = [request.customer?.first_name, request.customer?.last_name].filter(Boolean).join(' ').toLowerCase();
    const serviceType = request.service_type.toLowerCase();
    return customerName.includes(searchLower) || serviceType.includes(searchLower);
  });

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      render: (id: number) => `#${id}`,
    },
    {
      title: 'Customer',
      key: 'customer',
      width: 200,
      render: (_: any, record: QuoteRequest) => (
        <div>
          <Text strong>
            {record.customer ? `${record.customer.first_name} ${record.customer.last_name}` : 'Unknown'}
          </Text>
          {record.customer?.email && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>{record.customer.email}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Service Type',
      dataIndex: 'service_type',
      key: 'service_type',
      width: 150,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 200,
      render: (text: string) => (
        <Tooltip title={text}>
          <Text ellipsis>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Urgency',
      dataIndex: 'urgency',
      key: 'urgency',
      width: 100,
      render: (urgency: string) => getUrgencyTag(urgency),
      filters: [
        { text: 'Normal', value: 'normal' },
        { text: 'Urgent', value: 'urgent' },
        { text: 'Emergency', value: 'emergency' },
      ],
      onFilter: (value: any, record: QuoteRequest) => record.urgency === value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'Assigned To',
      key: 'assigned_to',
      width: 120,
      render: (_: any, record: QuoteRequest) => (
        record.assigned_user ? (
          <Text>{record.assigned_user.first_name} {record.assigned_user.last_name}</Text>
        ) : (
          <Text type="secondary">Unassigned</Text>
        )
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: QuoteRequest) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => viewDetails(record)}
            />
          </Tooltip>
          {record.status === 'pending' && (
            <Tooltip title="Assign">
              <Select
                size="small"
                placeholder="Assign"
                style={{ width: 90 }}
                onChange={(userId) => handleAssign(record.id, userId)}
                suffixIcon={<UserAddOutlined />}
              >
                {users.map(user => (
                  <Select.Option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name}
                  </Select.Option>
                ))}
              </Select>
            </Tooltip>
          )}
          {!record.estimate_id && record.status !== 'declined' && (
            <Tooltip title="Create Estimate">
              <Button
                size="small"
                type="primary"
                icon={<FileTextOutlined />}
                onClick={() => handleCreateEstimate(record)}
              >
                Estimate
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '100%' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header with Breadcrumb */}
        <div>
          <Breadcrumb
            items={[
              {
                href: `${rolePrefix}/dashboard`,
                title: <HomeOutlined />,
              },
              {
                title: 'Quote Requests',
              },
            ]}
            style={{ marginBottom: 16 }}
          />
          <Row justify="space-between" align="middle" gutter={[16, 16]}>
            <Col xs={24} sm={16}>
              <Space direction="vertical" size={0}>
                <Title level={2} style={{ margin: 0 }}>
                  <MessageOutlined /> Quote Requests
                </Title>
                <Text type="secondary">
                  Manage customer quote requests, assign to team members, and create estimates
                </Text>
              </Space>
            </Col>
            <Col xs={24} sm={8} style={{ textAlign: 'right' }}>
              <Badge count={pendingCount} showZero>
                <Button icon={<ClockCircleOutlined />} size="large">
                  {pendingCount} Pending
                </Button>
              </Badge>
            </Col>
          </Row>
        </div>

        {/* Filters Card */}
        <Card>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={10}>
              <Search
                placeholder="Search by customer or service..."
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                prefix={<SearchOutlined />}
              />
            </Col>
            <Col xs={12} sm={6} md={6}>
              <Select
                placeholder="Filter by status"
                allowClear
                style={{ width: '100%' }}
                value={statusFilter}
                onChange={setStatusFilter}
              >
                <Select.Option value="pending">Pending</Select.Option>
                <Select.Option value="assigned">Assigned</Select.Option>
                <Select.Option value="quoted">Quoted</Select.Option>
                <Select.Option value="declined">Declined</Select.Option>
              </Select>
            </Col>
            <Col xs={12} sm={6} md={8} style={{ textAlign: 'right' }}>
              <Space>
                <Text type="secondary">{filteredRequests.length} requests</Text>
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => {
                    setSearchText('');
                    setStatusFilter(undefined);
                  }}
                >
                  Clear Filters
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Requests Table */}
        <Card>
          <Table
            columns={columns}
            dataSource={filteredRequests}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} requests`,
              responsive: true,
            }}
            scroll={{ x: 1200 }}
            size="middle"
          />
        </Card>
      </Space>

      {/* Request Details Drawer */}
      <Drawer
        title="Quote Request Details"
        placement="right"
        open={detailsVisible}
        onClose={() => setDetailsVisible(false)}
        width={window.innerWidth < 768 ? '100%' : 600}
      >
        {selectedRequest && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Card size="small" title="Request Information">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Request ID">#{selectedRequest.id}</Descriptions.Item>
                <Descriptions.Item label="Status">{getStatusTag(selectedRequest.status)}</Descriptions.Item>
                <Descriptions.Item label="Urgency">{getUrgencyTag(selectedRequest.urgency)}</Descriptions.Item>
                <Descriptions.Item label="Service Type">{selectedRequest.service_type}</Descriptions.Item>
                <Descriptions.Item label="Created">
                  {new Date(selectedRequest.created_at).toLocaleString()}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card size="small" title="Customer Information">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Name">
                  {selectedRequest.customer ?
                    `${selectedRequest.customer.first_name} ${selectedRequest.customer.last_name}` :
                    'Unknown'}
                </Descriptions.Item>
                <Descriptions.Item label="Email">{selectedRequest.customer?.email}</Descriptions.Item>
                <Descriptions.Item label="Phone">{selectedRequest.customer?.phone}</Descriptions.Item>
              </Descriptions>
            </Card>

            {selectedRequest.vehicle && (
              <Card size="small" title="Vehicle Information">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Vehicle">
                    {selectedRequest.vehicle.year} {selectedRequest.vehicle.make} {selectedRequest.vehicle.model}
                  </Descriptions.Item>
                  <Descriptions.Item label="VIN">{selectedRequest.vehicle.vin}</Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            <Card size="small" title="Request Details">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>Description:</Text>
                  <Paragraph style={{ marginTop: 8 }}>{selectedRequest.description}</Paragraph>
                </div>
                {selectedRequest.customer_notes && (
                  <div>
                    <Text strong>Customer Notes:</Text>
                    <Paragraph style={{ marginTop: 8 }}>{selectedRequest.customer_notes}</Paragraph>
                  </div>
                )}
                {selectedRequest.preferred_contact_time && (
                  <div>
                    <Text strong>Preferred Contact Time:</Text>
                    <Paragraph style={{ marginTop: 8 }}>{selectedRequest.preferred_contact_time}</Paragraph>
                  </div>
                )}
              </Space>
            </Card>

            {selectedRequest.assigned_user && (
              <Card size="small" title="Assignment">
                <Text>Assigned to: <strong>{selectedRequest.assigned_user.first_name} {selectedRequest.assigned_user.last_name}</strong></Text>
              </Card>
            )}

            <Space style={{ width: '100%', justifyContent: 'flex-end' }} wrap>
              {!selectedRequest.estimate_id && selectedRequest.status !== 'declined' && (
                <Button
                  type="primary"
                  icon={<FileTextOutlined />}
                  onClick={() => handleCreateEstimate(selectedRequest)}
                >
                  Create Estimate
                </Button>
              )}
              {selectedRequest.status === 'pending' && (
                <Popconfirm
                  title="Are you sure you want to decline this request?"
                  onConfirm={() => handleUpdateStatus(selectedRequest.id, 'declined')}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button danger icon={<CloseCircleOutlined />}>
                    Decline
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </Space>
        )}
      </Drawer>
    </div>
  );
};

export default QuoteRequestsPage;
